import { z } from 'zod'
import type { ContractSourceMeta } from './questionnaire'

export const DEFAULT_SHARE_MAX_RESPONSES = 1000000

export const EmbedModeSchema = z.enum(['iframe', 'popup', 'slider', 'popover'])
export type EmbedMode = z.infer<typeof EmbedModeSchema>

export type ShareSettingsContract = {
  formId: string
  publicUrl: string
  isEnabled: boolean
  closeAt: string | null
  maxResponses: number | null
  allowRepeat: boolean
  defaultSalesCode: string
  defaultCampaignCode: string
  hiddenFields: Record<string, string>
  qrCodeUrl: string
  sourceMeta: ContractSourceMeta
}

export type EmbedSnippetContract = {
  mode: EmbedMode
  label: string
  code: string
  sourceMeta: ContractSourceMeta
}

export type TrackingLinkContract = {
  id: string
  formId: string
  name: string
  url: string
  utmSource: string
  utmMedium: string
  utmCampaign: string
  salesCode: string
  campaignCode: string
  hiddenFields: Record<string, string>
  sourceMeta: ContractSourceMeta
}

export const EmailShareProviderSchema = z.enum(['webhook', 'gmail_smtp'])
export type EmailShareProvider = z.infer<typeof EmailShareProviderSchema>

export const EmailShareTemplateCategorySchema = z.enum([
  'general_invitation',
  'b2b_customer_onboarding',
  'event_feedback',
  'sales_followup',
])
export type EmailShareTemplateCategory = z.infer<typeof EmailShareTemplateCategorySchema>

export type EmailShareRecipientContract = {
  id: string
  email: string
  name: string
  role: 'to' | 'cc'
  source: 'manual' | 'sales_user' | 'current_user'
  salesUserId?: string
  isValid: boolean
}

export type EmailShareTemplateContract = {
  id: string
  formId: string | null
  name: string
  category: EmailShareTemplateCategory
  description: string
  subject: string
  body: string
  defaultCcStrategy: 'none' | 'selected_sales_users' | 'form_sales_owner'
  defaultCcSalesUserIds: string[]
  variables: string[]
  updatedAt: string
  sourceMeta: ContractSourceMeta
}

export type EmailShareDraftContract = {
  formId: string
  templateId: string
  to: EmailShareRecipientContract[]
  cc: EmailShareRecipientContract[]
  subject: string
  body: string
  trackingUrl: string
  provider: EmailShareProvider
  providerMode: 'mock' | 'live'
}

export type EmailProviderEnvVarContract = {
  key: string
  description: string
  example: string
  requiredFor: EmailShareProvider[]
  secret: boolean
  location: '.dev.vars' | 'Cloudflare Pages encrypted env' | '.env for local tooling'
}

export type EmailProviderSetupContract = {
  provider: EmailShareProvider
  label: string
  status: 'provider_not_configured' | 'mock_ready' | 'ready'
  recommendedUse: string
  warning: string
  envVars: EmailProviderEnvVarContract[]
  sourceMeta: ContractSourceMeta
}

export type EmailSharePreviewContract = {
  formId: string
  provider: EmailShareProvider
  providerMode: 'mock'
  providerState: 'provider_not_configured'
  resolvedSubject: string
  resolvedBody: string
  resolvedUrl: string
  to: EmailShareRecipientContract[]
  cc: EmailShareRecipientContract[]
  warnings: string[]
  providerSetups: EmailProviderSetupContract[]
  sourceMeta: ContractSourceMeta
}

export const TrackingLinkInputSchema = z.object({
  formId: z.string(),
  name: z.string().min(1),
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
  salesCode: z.string().optional(),
  campaignCode: z.string().optional(),
  hiddenFields: z.record(z.string()).default({}),
})
export type TrackingLinkInput = z.input<typeof TrackingLinkInputSchema>

export const EmailSharePreviewInputSchema = z.object({
  formId: z.string(),
  templateId: z.string(),
  to: z.array(z.string()).default([]),
  cc: z.array(z.string()).default([]),
  subject: z.string().min(1),
  body: z.string().min(1),
  trackingUrl: z.string().url(),
  provider: EmailShareProviderSchema.default('webhook'),
})
export type EmailSharePreviewInput = z.input<typeof EmailSharePreviewInputSchema>

export const IntegrationProviderSchema = z.enum(['email_notifications', 'webhook'])
export type IntegrationProvider = z.infer<typeof IntegrationProviderSchema>

export type IntegrationSettingContract = {
  key: IntegrationProvider
  label: string
  description: string
  status: 'not_configured' | 'mock_configured'
  fields: Array<{
    key: string
    label: string
    placeholder: string
    type: 'text' | 'email' | 'url' | 'password'
  }>
  testActionLabel: string
  sourceMeta: ContractSourceMeta
}

export const integrationProviderOptions: Array<
  Omit<IntegrationSettingContract, 'status' | 'sourceMeta'>
> = [
  {
    key: 'email_notifications',
    label: 'Email 通知',
    description: '問卷送出、主管審核、匯出完成時通知指定收件人。',
    fields: [
      { key: 'recipients', label: '收件人 Email', placeholder: 'ops@example.com', type: 'email' },
      { key: 'replyTo', label: 'Reply-To', placeholder: 'support@example.com', type: 'email' },
    ],
    testActionLabel: '寄送測試信',
  },
  {
    key: 'webhook',
    label: 'Webhook',
    description: '回應完成或建檔核准後，以 HMAC 簽名推送事件。',
    fields: [
      {
        key: 'url',
        label: 'Webhook URL',
        placeholder: 'https://api.example.com/miaoform',
        type: 'url',
      },
      { key: 'secret', label: '簽名密鑰', placeholder: '未接線前不儲存真實密鑰', type: 'password' },
    ],
    testActionLabel: '送出測試事件',
  },
]
